import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Link,
  List,
  ListItem,
  Typography,
} from '@mui/material'
import {
  formatNonPurchasableBannerSummary,
  NON_PURCHASABLE_BANNER_PREVIEW_LIMIT,
} from '../utils/shipmentAvailabilityWarning'

/**
 * Sticky warning above the ship table when included lines are not purchasable.
 * Lists blocked articles (collapsed + View all) and offers remove-all.
 */
export default function NonPurchasableShipmentBanner({
  items = [],
  disabled = false,
  onRemoveAll,
  onFocusItem,
}) {
  const [expanded, setExpanded] = useState(false)

  if (!items.length) return null

  const previewLimit = NON_PURCHASABLE_BANNER_PREVIEW_LIMIT
  const visible = expanded ? items : items.slice(0, previewLimit)
  const hiddenCount = Math.max(0, items.length - previewLimit)

  return (
    <Alert
      severity="error"
      sx={{ mb: 2, alignItems: 'flex-start' }}
      data-testid="non-purchasable-shipment-banner"
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Cannot ship — non-purchasable articles
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {formatNonPurchasableBannerSummary(items.length)} Set their ship quantity
        to 0, remove them from this shipment, or mark them purchasable in product
        settings — then try again.
      </Typography>

      <List
        dense
        disablePadding
        sx={{
          mb: 1,
          maxWidth: '100%',
          ...(expanded && items.length > previewLimit
            ? { maxHeight: 160, overflowY: 'auto', pr: 0.5 }
            : null),
        }}
      >
        {visible.map((item) => (
          <ListItem
            key={item.key}
            disableGutters
            sx={{
              display: 'list-item',
              listStyleType: 'disc',
              ml: 2.5,
              py: 0.25,
            }}
          >
            {onFocusItem ? (
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={() => onFocusItem(item)}
                sx={{
                  textAlign: 'left',
                  color: 'inherit',
                  fontWeight: 500,
                }}
              >
                {item.label}
              </Link>
            ) : (
              <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                {item.label}
              </Typography>
            )}
          </ListItem>
        ))}
      </List>

      {hiddenCount > 0 && !expanded && (
        <Link
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          onClick={() => setExpanded(true)}
          sx={{ display: 'inline-block', mb: 1.25, fontWeight: 600 }}
        >
          View all ({items.length})
        </Link>
      )}
      {expanded && items.length > previewLimit && (
        <Link
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          onClick={() => setExpanded(false)}
          sx={{ display: 'inline-block', mb: 1.25, fontWeight: 600 }}
        >
          Show less
        </Link>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={disabled}
          onClick={onRemoveAll}
        >
          Remove all non-purchasable
        </Button>
      </Box>
    </Alert>
  )
}
